import type { MarkerNoteFormat } from '../core/types';

export const NOTE_ZOOM_LEVELS = [75, 85, 100, 115, 130, 150, 175, 200] as const;
const NOTE_ZOOM_STORAGE_PREFIX = 'liveMediaPlayer.notes.zoom.';

export function clampNoteZoom(percent: number): number {
  if (!Number.isFinite(percent)) return 100;
  return Math.min(NOTE_ZOOM_LEVELS.at(-1)!, Math.max(NOTE_ZOOM_LEVELS[0], percent));
}

export function getNoteZoom(format: MarkerNoteFormat): number {
  try {
    const stored = Number.parseFloat(localStorage.getItem(`${NOTE_ZOOM_STORAGE_PREFIX}${format}`) ?? '');
    return Number.isFinite(stored) ? clampNoteZoom(stored) : 100;
  } catch {
    return 100;
  }
}

export function stepNoteZoom(format: MarkerNoteFormat, direction: -1 | 1): number {
  const current = getNoteZoom(format);
  const next =
    direction === 1
      ? (NOTE_ZOOM_LEVELS.find((level) => level > current) ?? NOTE_ZOOM_LEVELS.at(-1)!)
      : ([...NOTE_ZOOM_LEVELS].reverse().find((level) => level < current) ?? NOTE_ZOOM_LEVELS[0]);
  try {
    localStorage.setItem(`${NOTE_ZOOM_STORAGE_PREFIX}${format}`, String(next));
  } catch {
    // Zoom still applies to this render when persistent storage is unavailable.
  }
  return next;
}

export function applyNoteZoom(element: HTMLElement): void {
  const textScale = getNoteZoom('text') / 100;
  const sheetScale = getNoteZoom('sheet') / 100;
  const pixels = (value: number): string => `${Number(value.toFixed(3))}px`;
  element.style.setProperty('--note-text-font-size', pixels(18 * textScale));
  element.style.setProperty('--note-text-line-height', String(1.55 - Math.max(0, textScale - 1) * 0.12));
  // The raw ratio (not baked into an absolute px value like the variable
  // above) — .notes-next-preview scales its own smaller base size off this
  // directly, rather than sharing Current's 18px size outright, which would
  // erase the deliberate size difference between the primary editing
  // surface and the smaller look-ahead preview.
  element.style.setProperty('--note-text-zoom-scale', String(Number(textScale.toFixed(3))));
  element.style.setProperty('--note-sheet-font-size', pixels(16 * sheetScale));
  element.style.setProperty('--note-sheet-cell-min-width', pixels(54 * sheetScale));
  element.style.setProperty('--note-sheet-cell-height', pixels(34 * sheetScale));
  element.style.setProperty('--note-sheet-cell-padding-y', pixels(6 * sheetScale));
  element.style.setProperty('--note-sheet-cell-padding-x', pixels(9 * sheetScale));
  element.style.setProperty('--note-sheet-preview-font-size', pixels(12 * sheetScale));
}

export function renderNoteZoomControl(format: MarkerNoteFormat, dataAttributes = ''): string {
  const zoom = getNoteZoom(format);
  const label = format === 'text' ? 'Text size' : 'Sheet zoom';
  const decreaseIcon =
    '<svg class="note-zoom-icon" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M5 10h10" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>';
  const increaseIcon =
    '<svg class="note-zoom-icon" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M10 5v10M5 10h10" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>';
  return `<div class="note-zoom-control" role="group" aria-label="${label}" ${dataAttributes}>
    <button type="button" data-action="adjust-note-zoom" data-note-format="${format}" data-zoom-direction="-1" ${dataAttributes} aria-label="Decrease ${label.toLowerCase()}" ${zoom <= NOTE_ZOOM_LEVELS[0] ? 'disabled' : ''}>${decreaseIcon}</button>
    <output class="note-zoom-value" aria-live="polite">${zoom}%</output>
    <button type="button" data-action="adjust-note-zoom" data-note-format="${format}" data-zoom-direction="1" ${dataAttributes} aria-label="Increase ${label.toLowerCase()}" ${zoom >= NOTE_ZOOM_LEVELS.at(-1)! ? 'disabled' : ''}>${increaseIcon}</button>
  </div>`;
}
